<template>
  <b-modal
    ref="modalRef"
    :visible="visible"
    :title="$t('involvedModal.edit.title')"
    size="lg"
    centered
    hide-footer
    no-close-on-backdrop
    @change="$emit('update:visible', $event)"
    @shown="onShown"
  >
    <div
      v-if="reuse"
      class="alert py-1 px-2 d-flex justify-content-between align-items-center"
      :class="canMerge ? 'alert-info' : 'alert-warning'"
    >
      <span class="small">
        <feather-icon
          icon="LinkIcon"
          size="14"
          class="mr-25"
        />
        {{ canMerge ? $t('involvedModal.edit.lookup.foundMerge') : $t('involvedModal.edit.lookup.foundOther') }}
        <strong>{{ reuse.display_label }}</strong>
        <small
          v-if="canMerge"
          class="d-block"
        >{{ $t('involvedModal.edit.lookup.hint') }}</small>
      </span>
      <button
        v-if="canMerge"
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="undoLookup"
      >
        {{ $t('involvedModal.edit.lookup.undo') }}
      </button>
    </div>
    <div
      v-else-if="lookupLoading"
      class="text-muted small mb-2"
    >
      <b-spinner small /> {{ $t('involvedModal.edit.lookup.searching') }}
    </div>

    <InvolvedPersonForm
      v-if="form"
      v-model="form"
      :errors="errors"
      @lookup-key="onKeyLookup"
    />

    <div class="d-flex justify-content-between align-items-center mt-2">
      <button
        type="button"
        class="btn btn-outline-secondary"
        @click="$refs.modalRef.hide()"
      >
        {{ $t('involvedModal.edit.buttons.close') }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="saving"
        @click="submit"
      >
        <b-spinner
          v-if="saving"
          small
          class="mr-25"
        />
        <feather-icon
          v-else
          icon="SaveIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('involvedModal.edit.buttons.save') }}
      </button>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from 'bootstrap-vue'
import InvolvedPersonForm from './InvolvedPersonForm.vue'

const PERSON_FIELDS = [
  'unidentified', 'name', 'apparent_description', 'age', 'age_unit', 'gender',
  'social_name', 'cpf', 'rg', 'cns', 'birth_date', 'race', 'telephone', 'cellphone',
]

export default {
  name: 'OccurrenceInvolvedEditModal',

  components: { BModal, BSpinner, InvolvedPersonForm },

  props: {
    visible: { type: Boolean, default: false },
    /** occurrence_involved (com `.entity` carregado) da pessoa a editar. */
    involved: { type: Object, default: null },
  },

  data() {
    return {
      form: null,
      originalForm: null,
      errors: {},
      saving: false,
      lookupLoading: false,
      reuse: null,
    }
  },

  computed: {
    // Só o envolvido "não identificado" é unificado com o cadastro existente ao salvar.
    isUnidentified() {
      return !!(this.involved && this.involved.entity && this.involved.entity.unidentified)
    },
    // Pode virar o cadastro encontrado: o não identificado ou a pessoa que ainda não tem CPF (o backend faz a mesma conta).
    canMerge() {
      const entity = (this.involved && this.involved.entity) || {}

      return this.isUnidentified || !entity.cpf
    },
  },

  methods: {
    onShown() {
      const entity = (this.involved && this.involved.entity) || {}
      const form = {}
      PERSON_FIELDS.forEach(f => {
        form[f] = entity[f] ?? (f === 'age_unit' ? this.$t('involvedModal.edit.ageUnit.default') : '')
      })
      form.unidentified = !!entity.unidentified
      this.form = form
      this.originalForm = { ...form }
      this.errors = {}
      this.reuse = null
      this.lookupLoading = false
    },

    // Ao sair do CPF, procura cadastro existente (mesma busca do "Adicionar envolvido").
    async onKeyLookup(value) {
      const key = (value || '').toString().trim()
      const entity = (this.involved && this.involved.entity) || {}
      if (!key || this.reuse || key === (entity.cpf || '')) return

      this.lookupLoading = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}involved/lookup`, {
          params: { entity_type: 'person', key, key_type: 'cpf' },
        })
        const found = res.data.data || null
        // Achou a própria pessoa que está sendo editada: nada a fazer.
        if (!found || found.entity_id === (this.involved && this.involved.entity_id)) return

        this.reuse = found
        // Preencher com os dados de outra pessoa só é seguro quando o backend vai
        // unificar os cadastros (não identificado ou sem CPF); numa pessoa que já tem
        // CPF isso sobrescreveria o cadastro dela, então só avisa (e o salvar recusa).
        if (this.canMerge) this.fillFormFromLookup(found)
      } catch (e) {
        // silencioso: se a busca falhar, segue como edição normal
      } finally {
        this.lookupLoading = false
      }
    },

    fillFormFromLookup(found) {
      const skip = ['entity_type', 'entity_id', 'display_label']
      Object.keys(found).forEach(k => {
        if (!skip.includes(k) && k in this.form) this.$set(this.form, k, found[k])
      })
    },

    undoLookup() {
      this.form = { ...this.originalForm }
      this.reuse = null
    },

    async submit() {
      const entityId = this.involved && this.involved.entity_id
      if (!entityId) return

      this.saving = true
      try {
        // occurrence_id não altera o cadastro: serve para o registro de alterações saber em qual ocorrência foi a correção.
        const res = await this.$http.put(`${this.$store.state.api}involveds/${entityId}`, {
          ...this.form,
          occurrence_id: this.involved.occurrence_id,
        })
        if (res.data && res.data.merged) {
          this.$message(this.$t('involvedModal.edit.messages.merged.title'), this.$t('involvedModal.edit.messages.merged.body'), 'success')
        } else {
          this.$message(this.$t('involvedModal.edit.messages.success.title'), this.$t('involvedModal.edit.messages.success.body'), 'success')
        }
        this.$emit('edited')
        this.$refs.modalRef.hide()
      } catch (e) {
        const data = (e.response && e.response.data) || null
        const validation = data && data.errors ? Object.values(data.errors)[0][0] : null
        const msg = validation || data || this.$t('involvedModal.edit.messages.error.body')
        this.$message(this.$t('involvedModal.edit.messages.error.title'), typeof msg === 'string' ? msg : this.$t('involvedModal.edit.messages.error.body'), 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
